// src/lib/supabase.js - Enhanced with proper session management
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Enable automatic session refresh
    autoRefreshToken: true,
    // Persist session in localStorage
    persistSession: true,
    // Enable session detection across tabs
    detectSessionInUrl: true,
    // Reduce refresh threshold to catch issues earlier
    refreshThreshold: 60, // Refresh 60 seconds before expiry
  },
})

// Add visibility change listener to handle tab switching
if (typeof window !== 'undefined') {
  // Handle visibility changes to manage session refresh
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      console.log('🔄 Tab became visible, checking session...');
      // Trigger session check when tab becomes visible
      supabase.auth.getSession().then(({ data: { session }, error }) => {
        if (error) {
          console.error('❌ Session check error:', error);
        } else {
          console.log('✅ Session check:', session ? 'Valid' : 'None');
        }
      });
    }
  });

  // Handle page focus to ensure session is fresh
  window.addEventListener('focus', () => {
    console.log('🔄 Window focused, ensuring session refresh...');
    supabase.auth.startAutoRefresh();
  });

  // Optional: Stop auto refresh when page loses focus (saves resources)
  window.addEventListener('blur', () => {
    console.log('😴 Window blurred');
    // Don't stop auto refresh as it might cause issues
    // supabase.auth.stopAutoRefresh();
  });
}

// Debug function for development
export const debugAuth = async () => {
  if (import.meta.env.DEV) {
    console.log('=== 🔍 AUTH DEBUG ===');
    const { data: { session }, error } = await supabase.auth.getSession();
    console.log('Current session:', session);
    console.log('Session error:', error);
    console.log('User:', session?.user);
    console.log('Access token expires at:', session?.expires_at ? new Date(session.expires_at * 1000) : 'N/A');
    console.log('LocalStorage auth key:', Object.keys(localStorage).filter(key => key.includes('supabase')));
    console.log('========================');
  }
};

// Make debug available in development
if (import.meta.env.DEV) {
  window.debugAuth = debugAuth;
  // Auto-debug on auth state changes in development
  supabase.auth.onAuthStateChange((event, session) => {
    console.log(`🔔 [DEBUG] Auth event: ${event}`, session ? 'Session exists' : 'No session');
  });
}